import React, { useCallback, useEffect, useState } from 'react'
import { StyleSheet, View, Platform } from 'react-native'
import { Chip } from 'react-native-paper'

export interface FilterOption<T = string> {
  value: T
  label: string
}

export interface FilterChipsProps<T = string> {
  options: FilterOption<T>[]
  selectedValue?: T
  onValueChange: (value: T) => void
  queryParamName?: string // Optional: enable URL query param sync (web only)
}

/**
 *https://www.notion.so/coderbunker/Filter-Chips-2960892b6c3080fd9d1dc3845d1824aa?source=copy_link
 */
export function FilterChips<T = string>({
  options,
  selectedValue,
  onValueChange,
  queryParamName = 'tab',
}: FilterChipsProps<T>) {
  // Get initial value from URL query param if enabled (web only)
  const getInitialValue = useCallback((): void => {
    if (
      queryParamName &&
      Platform.OS === 'web' &&
      typeof window !== 'undefined'
    ) {
      const params = new URLSearchParams(window.location.search)
      const paramValue = params.get(queryParamName)
      if (paramValue) {
        const matchingOption = options.find(
          (opt) => String(opt.value) === paramValue
        )
        if (matchingOption) {
          onValueChange(matchingOption.value)
        }
      }
    }
  }, [onValueChange, queryParamName, options])

  useEffect(() => {
    getInitialValue()
  }, [])

  // Update URL when value changes (web only)
  useEffect(() => {
    if (
      queryParamName &&
      Platform.OS === 'web' &&
      typeof window !== 'undefined'
    ) {
      const url = new URL(window.location.href)
      url.searchParams.set(queryParamName, String(selectedValue))
      window.history.replaceState({}, '', url.toString())
    }
  }, [selectedValue, queryParamName])

  const handleValueChange = (value: T) => {
    onValueChange(value)
  }

  return (
    <View style={styles.container}>
      {options.map((option) => (
        <Chip
          key={String(option.value)}
          selected={selectedValue === option.value}
          onPress={() => handleValueChange(option.value)}
          style={styles.chip}
        >
          {option.label}
        </Chip>
      ))}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 8,
  },
  chip: {
    borderRadius: 16,
  },
})
